import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IPlan, SubscriptionType } from "~/types/billing";
import { usePlans } from "~/api/billing/queries";
import PlanCard from "./planCard";
import { PlansSkeleton } from "~/components/skeletons/PlansSkeleton";
import * as S from "./plans.style";
import { useAccount } from "~/hooks/useAccount";
import { TogglePlan } from "./togglePlan";

type Props = {
  closeModal?: () => void;
  initialIsAnnual?: boolean;
  handleClickCheckout?: (plan: IPlan) => void;
};

export const Plans = ({
  closeModal,
  initialIsAnnual = false,
  handleClickCheckout = undefined,
}: Props) => {
  const { t } = useTranslation();
  const { data: plans, isLoading } = usePlans();
  const { account } = useAccount();

  const isCurrentPlanAnnual =
    account?.active_subscription?.plan?.interval === SubscriptionType.ANNUAL;

  const defaultIsAnnual =
    initialIsAnnual ||
    account?.active_subscription?.plan?.interval === SubscriptionType.ANNUAL;

  const [isAnnual, setIsAnnual] = useState<boolean>(defaultIsAnnual);

  useEffect(() => {
    if (isCurrentPlanAnnual) {
      setIsAnnual(isCurrentPlanAnnual);
    }
  }, [account?.active_subscription?.plan?.interval]);

  const activePlans = useMemo(() => {
    return plans?.filter((plan) => plan.status === "AC");
  }, [plans]);

  const activeSubscriptionPlanId =
    account?.payment_provider === "stripe"
      ? account?.last_subscription?.plan?.id
      : account?.active_subscription?.plan?.id;

  const filteredPlans = useMemo(() => {
    if (isAnnual)
      return activePlans?.filter(
        (plan) => plan.interval === SubscriptionType.ANNUAL
      );

    const monthlyPlans = activePlans?.filter(
      (plan) => plan.interval === SubscriptionType.MONTHLY
    );

    const grouped = new Map<string, IPlan[]>();
    monthlyPlans?.forEach((plan) => {
      const key = plan.name;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(plan);
    });

    const result: IPlan[] = [];
    grouped.forEach((variants) => {
      const activeVariant = variants.find(
        (p) => p.id === activeSubscriptionPlanId
      );
      if (activeVariant) {
        result.push(activeVariant);
        return;
      }
      const winning = variants.find((p) => p.for_winning);
      if (winning) result.push(winning);
    });

    return result;
  }, [activePlans, isAnnual, activeSubscriptionPlanId]);

  const biggestMonthsOff = useMemo(() => {
    return plans?.reduce((maxMonths, plan) => {
      return Math.max(maxMonths, plan.months_off);
    }, 0);
  }, [plans]);
  const isInShopifyAppReview = useMemo(
    () => account?.is_annual_shopify || false,
    [account]
  );
  const toggleControl = useCallback(() => setIsAnnual(!isAnnual), [, isAnnual]);

  const highestPricePlan = useMemo(
    () =>
      filteredPlans?.reduce(
        (prev, current) =>
          current.cost_per_month > prev.cost_per_month ? current : prev,
        filteredPlans[0]
      ),
    [filteredPlans]
  );

  return (
    <Fragment>
      <S.PageTitle>{t("settings.choose_plan")}</S.PageTitle>

      {isLoading ? (
        <PlansSkeleton gap={closeModal === undefined ? 1 : 2} />
      ) : (
        <>
          <TogglePlan
            isAnnual={isAnnual}
            toggleControl={toggleControl}
            biggestMonthsOff={biggestMonthsOff}
          />

          <S.PlansGrid gap={closeModal === undefined ? 1 : 2}>
            {filteredPlans?.map((plan, index) => {
              let previousPlanName =
                index > 0 ? filteredPlans[index - 1].name : null;
              if (isAnnual && index === 0) {
                previousPlanName =
                  activePlans?.filter(
                    (plan) => plan.interval === SubscriptionType.MONTHLY
                  )[0].name || null;
              }

              return (
                <PlanCard
                  account={account}
                  key={plan.id}
                  plan={plan}
                  closeModal={closeModal}
                  isHighestPrice={plan.id === highestPricePlan?.id}
                  previousPlanName={previousPlanName}
                  handleClick={handleClickCheckout}
                />
              );
            })}
          </S.PlansGrid>
        </>
      )}
    </Fragment>
  );
};
